import { ICorporateNo } from '../../interfaces/types';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import NoCorporateNosIcon from '@cp/src/assets/icons/NoCorporateNumbersIcon';
import CorporateNoCard from './CorporateNoCard';
import { Typography } from '@navi/web-ui/lib/primitives';
import { NUMBER_STATUS } from '../../constants/constants';
import InfiniteScroll from '@cp/src/components/InfiniteScroll/InfiniteScroll';
import { useState, useMemo, useCallback, useEffect } from 'react';
import LoaderIcon from '@cp/src/assets/icons/LoaderIcon';
import { LoadingIcon } from '@navi/web-ui/lib/icons';

const ITEMS_PER_PAGE = 10;

const CorporateNumbers = ({ isCorporateNosLoading }: { isCorporateNosLoading: boolean }) => {
  const corporateNos = useSelector(
    (state: RootState) => state.teamLeadDashboard.agentCorporateNumbers.userNumberMappings
  );

  const [page, setPage] = useState<number | null>(null);
  const [paginatedInactiveNumbers, setPaginatedInactiveNumbers] = useState<ICorporateNo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const { activeNumbers, inActiveNumbers } = useMemo(() => {
    return corporateNos.reduce(
      (acc, corporateNo: ICorporateNo) => {
        if (corporateNo.status === NUMBER_STATUS.ACTIVE) {
          acc.activeNumbers.push(corporateNo);
        } else {
          acc.inActiveNumbers.push(corporateNo);
        }
        return acc;
      },
      { activeNumbers: [] as ICorporateNo[], inActiveNumbers: [] as ICorporateNo[] }
    );
  }, [corporateNos]);

  useEffect(() => {
    if (inActiveNumbers.length > 0) {
      setPaginatedInactiveNumbers(inActiveNumbers.slice(0, ITEMS_PER_PAGE));
      setHasMore(inActiveNumbers.length > ITEMS_PER_PAGE);
      setPage(ITEMS_PER_PAGE);
    } else {
      setPaginatedInactiveNumbers([]);
      setHasMore(false);
    }
  }, [inActiveNumbers]);

  useEffect(() => {
    if (page === null || isLoading) return;

    setIsLoading(true);

    const startIndex = page;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const newItems = inActiveNumbers.slice(startIndex, endIndex);

    setPaginatedInactiveNumbers(prev => [...prev, ...newItems]);

    if (endIndex >= inActiveNumbers.length) {
      setHasMore(false);
    } else {
      setPage(endIndex);
    }

    setIsLoading(false);
  }, [page, inActiveNumbers]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      setPage(prevPage => (prevPage !== null ? prevPage + ITEMS_PER_PAGE : null));
    }
  }, [isLoading, hasMore]);

  return (
    <>
      {isCorporateNosLoading ? (
        <div className="absolute h-full w-full bg-white top-0 flex items-center justify-center">
          <LoaderIcon
            size={40}
            borderWidth={4}
            borderColor="var(--navi-color-gray-border)"
            fillColor="var(--navi-color-blue-base)"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-5 h-full">
          {!corporateNos.length ? (
            <div className="flex flex-col justify-center items-center h-full">
              <NoCorporateNosIcon />
              <Typography variant="p3" color="var(--grayscale-2)">
                No corporate numbers available
              </Typography>
              <Typography variant="p3" color="var(--grayscale-2)">
                for this employee
              </Typography>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={paginatedInactiveNumbers.length}
              next={loadMore}
              hasMore={hasMore}
              loader={<>{isLoading && <LoadingIcon />}</>}
              scrollThreshold={0.8}
              endMessage={
                <Typography variant="p3" color="var(--grayscale-2)" className="mt-4 mb-8">
                  No more results!
                </Typography>
              }
            >
              {activeNumbers.length > 0 && (
                <>
                  <Typography variant="h5" color="var(--grayscale-1)">
                    Current active numbers
                  </Typography>
                  {activeNumbers.map((corporateNo, index) => (
                    <div key={index} className="flex flex-row gap-2 mt-3">
                      <Typography variant="p3" color="var(--grayscale-2)">
                        {index + 1}.
                      </Typography>
                      <CorporateNoCard corporateNo={corporateNo} />
                    </div>
                  ))}
                </>
              )}

              {inActiveNumbers.length > 0 && (
                <>
                  <Typography
                    variant="h5"
                    color="var(--grayscale-1)"
                    className={activeNumbers.length > 0 ? 'mt-4' : ''}
                  >
                    Inactive numbers
                  </Typography>
                  {paginatedInactiveNumbers.map((corporateNo, index) => (
                    <div key={index} className="flex flex-row gap-2 mt-3">
                      <Typography variant="p3" color="var(--grayscale-2)">
                        {index + 1}.
                      </Typography>
                      <CorporateNoCard corporateNo={corporateNo} />
                    </div>
                  ))}
                </>
              )}
            </InfiniteScroll>
          )}
        </div>
      )}
    </>
  );
};

export default CorporateNumbers;
